import { useEffect, useState } from 'react';
import { Wrench, Loader2, Plus, X, AlertTriangle, Ban, Pencil, Trash2, Check } from 'lucide-react';
import * as api from '../../lib/api';
import { useShiftStore } from '../../store/useShiftStore';
import { formatDate } from '../../lib/format';
import NoFacility from '../../components/attendant/NoFacility';
import { useAuthStore } from '../../store/useAuthStore';
import TicketEditor from '../../components/attendant/TicketEditor';

const CATEGORIES = ['gate', 'lighting', 'fence', 'surface', 'debris', 'damage', 'other'];
const PRIORITIES = ['low', 'normal', 'high', 'urgent'];

const PRIORITY_STYLES = {
  urgent: 'bg-rose-100 text-rose-800',
  high: 'bg-amber-100 text-amber-900',
  normal: 'bg-slate-100 text-slate-700',
  low: 'bg-slate-100 text-slate-500',
};

export default function TicketsPage() {
  const facilityId = useShiftStore((s) => s.facilityId);
  const facilities = useShiftStore((s) => s.facilities);
  const [tickets, setTickets] = useState([]);
  const [state, setState] = useState('loading');
  const [error, setError] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [openOnly, setOpenOnly] = useState(true);
  const [staff, setStaff] = useState([]);
  const canDelete = useAuthStore((s) => ['admin', 'super_admin'].includes(s.user?.role));

  const load = async () => {
    if (!facilityId) { setState('no_facility'); return; }
    setState('loading');
    try {
      const { tickets: list } = await api.listTickets(facilityId, openOnly);
      api.facilityStaff(facilityId).then((p) => setStaff(p.staff)).catch(() => setStaff([]));
      setTickets(list);
      setState('ready');
    } catch (err) {
      setError(err.message);
      setState('error');
    }
  };

  useEffect(() => { load(); }, [facilityId, openOnly]);

  if (state === 'no_facility') return <NoFacility hasAnyFacility={facilities.length > 0} />;

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-extrabold tracking-tight text-slate-900">Issues</h1>
          <p className="mt-1 text-sm text-slate-600">Maintenance and incident reports at this facility.</p>
        </div>
        <button type="button" onClick={() => setFormOpen(true)} className="btn-primary shrink-0">
          <Plus className="h-4 w-4" aria-hidden="true" /> Report
        </button>
      </div>

      <div className="mt-4 flex rounded-lg border border-slate-300 p-0.5" role="group" aria-label="Filter issues">
        {[[true, 'Open'], [false, 'Everything']].map(([value, label]) => (
          <button key={label} type="button" onClick={() => setOpenOnly(value)} aria-pressed={openOnly === value}
                  className={`rounded-md px-3 py-1.5 text-xs font-bold ${
                    openOnly === value ? 'bg-brand-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>
            {label}
          </button>
        ))}
      </div>

      {error && <p role="alert" className="mt-4 rounded-lg bg-rose-50 p-3 text-sm text-rose-800">{error}</p>}

      {state === 'loading' && (
        <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand-600" aria-hidden="true" /></div>
      )}

      {state === 'ready' && tickets.length === 0 && (
        <div className="mt-6 rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center">
          <Wrench className="mx-auto h-8 w-8 text-brand-300" aria-hidden="true" />
          <p className="mt-3 text-sm font-bold text-slate-900">
            {openOnly ? 'Nothing open' : 'No issues have been reported here'}
          </p>
          {openOnly && (
            <button type="button" onClick={() => setOpenOnly(false)}
                    className="mt-1 text-xs font-bold text-brand-700 hover:underline">
              Show resolved ones too
            </button>
          )}
        </div>
      )}

      <ul className="mt-4 space-y-3">
        {tickets.map((t) => (
          <li key={t.public_id} className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`rounded-full px-2 py-0.5 text-[12px] font-bold uppercase ${PRIORITY_STYLES[t.priority]}`}>
                {t.priority}
              </span>
              <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-slate-600">
                {t.category}
              </span>
              <span className="text-[12px] text-slate-500">
                {t.unit_number ? `Unit ${t.unit_number}` : 'Site-wide'}
              </span>
              {t.blocks_rental && (
                <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-bold text-rose-800">
                  <Ban className="h-3 w-3" aria-hidden="true" /> Unrentable
                </span>
              )}
            </div>
            <p className="mt-2 text-sm font-bold text-slate-900">{t.title}</p>
            {t.body && <p className="mt-1 text-xs leading-relaxed text-slate-600">{t.body}</p>}
            {t.resolution && (
              <p className="mt-2 rounded-lg bg-emerald-50 p-2 text-xs text-emerald-900">
                <Check className="mr-1 inline h-3 w-3" aria-hidden="true" />
                {t.resolution}
              </p>
            )}
            <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
              <p className="text-[12px] text-slate-400">
                {t.reporter_first_name} {t.reporter_last_name} · {formatDate(t.created_at.slice(0, 10))}
                {t.resolved_at && ` · closed ${formatDate(t.resolved_at.slice(0, 10))}`}
              </p>
              <div className="flex gap-2">
                <button type="button" onClick={() => setEditing(t)} className="btn-ghost py-1.5 text-xs">
                  <Pencil className="h-3.5 w-3.5" aria-hidden="true" /> Edit
                </button>
                {canDelete && (
                  <button type="button" className="btn-ghost py-1.5 text-xs text-rose-700"
                          onClick={async () => {
                            // A ticket is a record of something someone saw. Resolving
                            // keeps it; deleting is for duplicates, so make it deliberate.
                            if (!window.confirm(
                              `Delete "${t.title}" for good?\n\n`
                              + 'Resolving it instead keeps the history. This cannot be undone.')) return;
                            try { await api.deleteTicket(facilityId, t.public_id); await load(); }
                            catch (err) { setError(err.message); }
                          }}>
                    <Trash2 className="h-3.5 w-3.5" aria-hidden="true" /> Delete
                  </button>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>

      {editing && (
        <TicketEditor
          ticket={editing} staff={staff}
          onClose={() => setEditing(null)}
          onSave={async (patch) => {
            await api.updateTicket(facilityId, editing.public_id, patch);
            setEditing(null);
            await load();
          }}
        />
      )}

      {formOpen && (
        <TicketForm
          facilityId={facilityId}
          onClose={() => setFormOpen(false)}
          onSaved={() => { setFormOpen(false); load(); }}
          onError={setError}
        />
      )}
    </div>
  );
}

function TicketForm({ facilityId, onClose, onSaved, onError }) {
  const [form, setForm] = useState({
    title: '', body: '', category: 'other', priority: 'normal', blocks_rental: false,
  });
  const [saving, setSaving] = useState(false);
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await api.createTicket(facilityId, form);
      onSaved();
    } catch (err) {
      onError(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4" role="dialog" aria-modal="true" aria-label="Report an issue">
      <form onSubmit={submit} className="max-h-full w-full max-w-md overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-start justify-between">
          <h2 className="text-lg font-extrabold text-slate-900">Report an issue</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-lg p-1 text-slate-400 hover:bg-slate-100">
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <label className="field-label mb-1" htmlFor="t-title">What happened</label>
        <input id="t-title" required autoFocus className="field-input mb-4" value={form.title} onChange={set('title')}
               placeholder="North gate motor stalling" />

        <div className="mb-4 grid grid-cols-2 gap-3">
          <div>
            <label className="field-label mb-1" htmlFor="t-cat">Category</label>
            <select id="t-cat" className="field-input" value={form.category} onChange={set('category')}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="field-label mb-1" htmlFor="t-pri">Priority</label>
            <select id="t-pri" className="field-input" value={form.priority} onChange={set('priority')}>
              {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        </div>

        <label className="field-label mb-1" htmlFor="t-body">Details</label>
        <textarea id="t-body" rows={4} className="field-input mb-4" value={form.body} onChange={set('body')}
                  placeholder="Takes about 40 seconds to close and stalls twice." />

        <label className="mb-5 flex cursor-pointer items-start gap-2.5 rounded-lg bg-amber-50 p-3">
          <input type="checkbox" checked={form.blocks_rental}
                 onChange={(e) => setForm({ ...form, blocks_rental: e.target.checked })}
                 className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500" />
          <span className="text-xs leading-snug text-amber-900">
            <span className="inline-flex items-center gap-1 font-bold">
              <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" /> This makes a space unrentable
            </span>
            <br />
            The unit is taken off the market immediately so nobody can book it.
          </span>
        </label>

        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="btn-ghost flex-1">Cancel</button>
          <button type="submit" disabled={saving} className="btn-primary flex-1">
            {saving && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            {saving ? 'Saving…' : 'Submit'}
          </button>
        </div>
      </form>
    </div>
  );
}
